import { useState } from "react";

const money = (v, cur) => new Intl.NumberFormat(cur === "INR" ? "en-IN" : "en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(v);
const Box = ({ children, testId }) => <div className="rounded-xl border border-black/10 bg-black/[0.03] p-3 text-sm" data-testid={testId}>{children}</div>;
const Range = ({ label, value, min, max, onChange, testId }) => (
  <label className="block text-xs text-slate-600">{label}: <b>{value}</b>
    <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-1 w-full accent-current" data-testid={testId} />
  </label>
);

const QtyTotal = ({ w, accent, qtyLabel, min = 1, max = 60, start = 10, factor = 1, note }) => {
  const [q, setQ] = useState(start);
  return (
    <Box testId="widget-body">
      <Range label={qtyLabel} value={q} min={min} max={max} onChange={setQ} testId="widget-range" />
      <p className="mt-2 font-semibold" style={{ color: accent }} data-testid="widget-result">{money(w.base_value * q * factor, w.currency)}</p>
      <p className="text-xs text-slate-500">{note}</p>
    </Box>
  );
};

const Choice = ({ w, accent, result }) => {
  const opts = w.options?.length ? w.options : ["Option A", "Option B", "Option C"];
  const [pick, setPick] = useState(opts[0]);
  return (
    <Box testId="widget-body">
      <div className="flex flex-wrap gap-2">
        {opts.map((o, i) => (
          <button key={o} type="button" onClick={() => setPick(o)} data-testid={`widget-option-${i}`}
            className="rounded-full border px-3 py-1 text-xs" style={pick === o ? { background: accent, color: "#fff", borderColor: accent } : {}}>{o}</button>
        ))}
      </div>
      <p className="mt-2 font-semibold" style={{ color: accent }} data-testid="widget-result">{result(pick, opts.indexOf(pick))}</p>
    </Box>
  );
};

const PinCode = ({ w, accent }) => {
  const [pin, setPin] = useState("");
  const valid = /^\d{5,6}$/.test(pin);
  const delta = valid ? ((Number(pin) % 17) - 8) / 100 : 0;
  return (
    <Box testId="widget-body">
      <input inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="Enter pin code"
        className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-slate-900" data-testid="widget-pincode-input" />
      <p className="mt-2 font-semibold" style={{ color: accent }} data-testid="widget-result">
        {valid ? `Basket ${money(w.base_value * (1 + delta), w.currency)} (${delta >= 0 ? "+" : ""}${Math.round(delta * 100)}% vs city avg)` : "Type a 5–6 digit pin code to simulate your basket"}
      </p>
    </Box>
  );
};

const RENDERERS = {
  pincode_basket_simulator: (w, a) => <PinCode w={w} accent={a} />,
  meal_plan_calculator: (w, a) => <QtyTotal w={w} accent={a} qtyLabel={`${w.unit_label}s per month`} start={20} max={44} note="Prepaid monthly plan total" />,
  roi_calculator: (w, a) => <QtyTotal w={w} accent={a} qtyLabel="Hours saved per week" start={10} max={80} factor={0.25} note="Indicative monthly value of time saved" />,
  seat_availability: (w, a) => <Choice w={w} accent={a} result={(o, i) => `${o}: ${[12, 7, 3, 9, 5, 2][i] ?? 4} seats free · ${money(w.base_value, w.currency)} per ${w.unit_label}`} />,
  session_booking: (w, a) => <Choice w={w} accent={a} result={(o) => `${o} reserved (preview) · ${money(w.base_value, w.currency)} per ${w.unit_label}`} />,
  unit_cost_estimator: (w, a) => <QtyTotal w={w} accent={a} qtyLabel="Order quantity (x100)" start={5} max={100} factor={100} note="Indicative order value before volume discounts" />,
  match_finder: (w, a) => <Choice w={w} accent={a} result={(o, i) => `${[14, 9, 21, 6][i] ?? 8} matches for ${o} · from ${money(w.base_value, w.currency)}`} />,
  eligibility_checker: (w, a) => <QtyTotal w={{ ...w, base_value: 800 }} accent={a} qtyLabel={`Requested amount (x1,000 ${w.currency})`} start={50} max={500} note="Indicative eligible amount (80% pre-check) · preview only, no data stored" />,
};

export const PreviewWidget = ({ widget, accent }) => (
  <section className="rounded-2xl border border-black/10 bg-white p-4 text-slate-900 shadow-sm" data-testid={`preview-widget-${widget.type}`}>
    <h3 className="mb-2 text-sm font-bold">{widget.title}</h3>
    {(RENDERERS[widget.type] || RENDERERS.roi_calculator)(widget, accent)}
  </section>
);
